import { CarouselMediaCardProps } from '@app/components/CarouselSection/types';
import { PriceProps } from '@app/components/Price/Price';
import { HomePageInventoryItem } from '@app/data/clients/inventoryHydrationServiceClient/queries/types';
import { FEATURED_PRODUCTS_PUBLIC_ENDPOINT } from '@app/pages/api/constants';
import { getDiscountTypeAndAmount } from '@app/utils/getPriceComponent';
import { defineMessages, IntlShape } from '@redbubble/next/intl';
import { getSafeUrl } from '@redbubble/utils/getSafeUrl';
import { useCallback, useState } from 'react';

export const messages = defineMessages({
  byArtist: {
    defaultMessage: 'by {artistName}',
  },
});

export const mapProductFeedInventoryItemToMediaCardProps = () => {
  return (
    inputData: HomePageInventoryItem,
    intl: IntlShape
  ): CarouselMediaCardProps | null => {
    if (!inputData) return null;
    const cardTitle = `${inputData.work.title} ${inputData.description}`;
    const artistName = inputData?.work.artistShopDisplayName;
    return {
      productId: inputData?.id,
      link:
        inputData.productPageUrls?.url ||
        inputData.productPageUrls?.fallbackUrl ||
        '',
      imageSrc: inputData.preview?.url,
      title: cardTitle,
      workTitle: inputData.work.title,
      artistName: artistName ?? undefined,
      matureContent: inputData?.work.matureContent ?? false,
      caption: intl.formatMessage(messages.byArtist, {
        artistName,
      }),
      loading: false,
      testId: `pf-media-card-${inputData.id}`,
      stockStatus: inputData.stockStatus,
    };
  };
};

export const mapPriceProps = (
  inputData: HomePageInventoryItem
): PriceProps | null => {
  if (!inputData.price || !inputData.volumeDiscount?.thresholds) return null;

  const priceTypeAndAmount = getDiscountTypeAndAmount(
    inputData.volumeDiscount.thresholds,
    inputData.price
  );

  if (!priceTypeAndAmount) {
    return null;
  }

  const { variant, discountedAmount } = priceTypeAndAmount;
  const { discount, currency, amount } = inputData.price;

  return {
    discount,
    currency,
    amount: variant === 'fromPriceStyle' ? discountedAmount : amount,
    variant,
  };
};

interface FeaturedProductsVariables {
  locale: string;
  country: string;
  currency: string;
}

const fetchFeaturedProducts = async (variables: FeaturedProductsVariables) => {
  const url = getSafeUrl(
    FEATURED_PRODUCTS_PUBLIC_ENDPOINT,
    window.location.href
  );

  if (!url) {
    throw new Error('Invalid URL for featured products endpoint');
  }

  Object.entries(variables).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  }).then((res) => {
    if (!res.ok) {
      throw new Error(`Error fetching featured products: ${res.statusText}`);
    }
    return res;
  });
  const data: HomePageInventoryItem[] = await response.json();
  return data;
};

export const useFeaturedProducts = () => {
  const [
    { featuredProducts, loading: featuredProductsLoading },
    setFeaturedProducts,
  ] = useState<{
    featuredProducts: HomePageInventoryItem[];
    loading: boolean;
  }>({
    featuredProducts: [],
    loading: true,
  });

  const refresh = useCallback((variables: FeaturedProductsVariables) => {
    setFeaturedProducts({
      featuredProducts: [],
      loading: true,
    });

    fetchFeaturedProducts(variables)
      .then((featuredProducts) => {
        setFeaturedProducts({
          featuredProducts,
          loading: false,
        });
      })
      .catch(() => {
        setFeaturedProducts({
          featuredProducts: [],
          loading: false,
        });
      });
  }, []);

  return {
    featuredProducts,
    loading: featuredProductsLoading,
    refresh: refresh,
  };
};
